// ============================================================================
// useDeploymentToken — F-014 (platform_admin per-deployment JWT mint)
// ============================================================================
// Mints a short-lived Hank-audience JWT scoped to a specific tenant, so a
// platform_admin can navigate into that tenant's Hank without Adam either
// (a) forging a tenant-scoped session or (b) relying on Hank exempting
// platform_admin from its tenant-match invariant. Option (b) from the
// live-vm-probe-2026-05-14.md decision.
//
// Cache lives in a process-local Map keyed by tenantId; tokens expire
// when their `expiresAt` passes (CP mints with a ~15min TTL). The hook
// never persists tokens — they live only in JS memory.
// ============================================================================

import { useCallback, useState } from "react";
import { mintDeploymentToken, type DeploymentTokenResult } from "../lib/api";

/** In-memory per-(adminSession, tenantId) deployment-token cache. */
const tokenCache = new Map<string, DeploymentTokenResult>();

/**
 * Buffer subtracted from `expiresAt` when deciding cache freshness so
 * a token that's about to expire isn't handed out for a fresh navigation.
 * 30s leaves ample headroom for the Hank request to land.
 */
const EXPIRY_BUFFER_MS = 30_000;

function getCachedToken(tenantId: string): DeploymentTokenResult | null {
  const cached = tokenCache.get(tenantId);
  if (!cached) return null;
  if (cached.expiresAt - EXPIRY_BUFFER_MS <= Date.now()) {
    tokenCache.delete(tenantId);
    return null;
  }
  return cached;
}

/** Test-only helper to clear the in-memory cache. */
export function __resetDeploymentTokenCacheForTests(): void {
  tokenCache.clear();
}

export interface UseDeploymentTokenState {
  /** Most recently minted (or cached) token for this tenantId, or null. */
  token: DeploymentTokenResult | null;
  /** Mint a fresh token (or return a cached one) for the given tenantId. */
  mint: () => Promise<DeploymentTokenResult>;
  /** Forget the cached token without minting a new one. */
  invalidate: () => void;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook for surfacing a per-tenant deployment token. Returns a cached
 * token when one exists AND hasn't expired; mints a new one otherwise.
 *
 * Usage pattern (operator clicks "Open Hank for tenant"):
 *   const { mint } = useDeploymentToken(tenant.id);
 *   const { token } = await mint();
 *   window.open(`${tenant.hankUrl}/?token=${token}`, "_blank");
 */
export function useDeploymentToken(tenantId: string): UseDeploymentTokenState {
  const [token, setToken] = useState<DeploymentTokenResult | null>(() => getCachedToken(tenantId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mint = useCallback(async (): Promise<DeploymentTokenResult> => {
    const cached = getCachedToken(tenantId);
    if (cached) {
      setToken(cached);
      return cached;
    }

    setLoading(true);
    setError(null);
    try {
      const minted = await mintDeploymentToken(tenantId);
      tokenCache.set(tenantId, minted);
      setToken(minted);
      return minted;
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      setError(wrapped);
      throw wrapped;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const invalidate = useCallback(() => {
    tokenCache.delete(tenantId);
    setToken(null);
  }, [tenantId]);

  return { token, mint, invalidate, loading, error };
}
